import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import { useT, useLanguage } from "../lib/LanguageContext";
import { formatMoneyCode } from "../lib/formatMoney";
import { tenantDisplayName, tenantOwnerEmail } from "./tenantDisplay";
import { tenantStatusLabel } from "./tenantStatus";
import { formatMoneyLines } from "./moneyDisplay";
import { validBilling } from "./billingCharge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ExternalLink, LayoutDashboard, Wallet, Percent, RefreshCcw } from "lucide-react";
import TrialTile from "./tenants/TrialTile";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { practiceConsoleHref } from "./practiceConsole";
import SectionsPanel from "./tenant360/SectionsPanel";
import FinancialHistory from "./tenant360/FinancialHistory";
import FinanceActionModal from "./tenant360/FinanceActionModal";
import CustomPriceBadge from "./tenant360/CustomPriceBadge";
import { moneyText } from "./tenant360/customPriceText";
import { instantDay } from "../lib/calendarDay";

/** One money figure, one line per currency — the label always names whose money it is. */
function MoneyTile({ testId, label, hint, lines, none, tone }) {
  const shown = formatMoneyLines(lines);
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testId}>
      <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</div>
      {shown.length === 0 ? (
        <div className="font-sol font-semibold text-2xl text-slate-400">{none}</div>
      ) : (
        shown.map((s) => (
          <div key={s} className={`font-sol font-semibold text-2xl ${tone}`}>{s}</div>
        ))
      )}
      <div className="text-[11px] text-slate-400 mt-1 leading-snug">{hint}</div>
    </div>
  );
}

/**
 * The two facts about the custom price that are not "a price is in force" (D4 contract section 8.1),
 * shown to every reader: a Stripe call whose outcome is still being checked (no other price change
 * is possible until then), and the custom price that just ended and why.
 */
function CustomPriceNotices({ pending, ended }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const pendingKind = pending && (pending.kind === "restore" ? "restore" : "set");
  return (
    <>
      {pending && (
        <p className="basis-full text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="custom-price-pending">
          {t(`t360f.pending.${pendingKind}`, {
            price: moneyText(pending.price), date: instantDay(pending.started_at, lang) || "—", by: pending.requested_by,
          })}
        </p>
      )}
      {ended && (
        <p className="basis-full text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="custom-price-ended">
          {t(`t360f.ended.${ended.reason}`, { price: moneyText(ended.price), date: instantDay(ended.at, lang) || "—" })}
        </p>
      )}
    </>
  );
}

export default function Tenant360() {
  const t = useT("superadmin");
  const { tenant_id } = useParams();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // Each write on this page is shown only to an operator the server lets make it.
  const writes = useScreenWrites("tenant360");
  const canRebate = writes.rebate;
  const canAdjust = writes.adjustPrice;
  const canRefund = writes.refund;
  const hasFinancialAction = canRebate || canAdjust || canRefund;

  // A practice that no longer exists gets its own not-found panel (a silent redirect to the
  // list hid why the page never opened); any other failure (a refusal included) is reported
  // here, where the operator asked for it.
  useEffect(() => {
    setLoadError(null);
    api.get(`/super-admin/tenants/${tenant_id}/360`).then((r) => setData(r.data)).catch(setLoadError);
  }, [tenant_id, attempt]);

  // Re-GET after a trial Extend/End, a finance action or a custom-price removal: a transient
  // failure on a REFRESH (as opposed to the initial load) must not navigate the operator away
  // from a page they are actively working on.
  const refetchTenant360 = useCallback(() => {
    api.get(`/super-admin/tenants/${tenant_id}/360`).then((r) => setData(r.data))
      .catch((e) => toast.error(extractApiError(e, t("t360.error"), t)));
  }, [tenant_id, t]);

  const [actionModal, setActionModal] = useState(null); // {type}
  // Bumped after every settled finance action and after a custom price is removed, so the
  // history below re-reads (a refused attempt may have written a row too).
  const [historyToken, setHistoryToken] = useState(0);
  const bumpHistory = useCallback(() => setHistoryToken((n) => n + 1), []);

  if (loadError?.response?.status === 404) {
    return (
      <div data-testid="tenant360-not-found">
        <Link to="/super-admin/tenants" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6">
          <ArrowLeft className="w-4 h-4" /> {t("t360.back")}
        </Link>
        <h1 className="font-sans font-semibold text-3xl mb-2 text-slate-800">{t("t360.notFound.title")}</h1>
        <p className="text-slate-500">{t("t360.notFound.body", { id: tenant_id })}</p>
      </div>
    );
  }
  if (loadError) return <PageLoadError error={loadError} onRetry={() => setAttempt((n) => n + 1)} testId="tenant360-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  const { tenant, subscription, plan, revenue_30d, billing_currency, custom_price, custom_price_pending, custom_price_ended } = data;
  const consoleHref = practiceConsoleHref(tenant.slug);  // null: no slug, no console address
  // What the practice is billed per cycle: the Tenants row's own block (BUG-074). `null` on
  // the wire is "billed nothing"; anything unreadable prints nothing rather than a claim.
  const billing = validBilling(data.billing);
  const notBilled = data.billing === null;

  return (
    <div data-testid="sa-tenant-360">
      <Link to="/super-admin/tenants" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("t360.back")}
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{tenantDisplayName(tenant)}</h1>
          <p className="text-slate-500">
            {/* Each separator belongs to the value BEFORE it, so an absent owner_email
                takes its own "·" with it instead of leaving "· ·" with nothing between. */}
            <code className="text-amber-600">/{tenant.slug}</code>
            {tenantOwnerEmail(tenant) && <> · {tenantOwnerEmail(tenant)}</>} ·
            <span className={`ml-2 inline-flex text-xs rounded-full px-2 py-0.5 ${tenant.status === "active" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`} data-testid="t360-status">
              {tenantStatusLabel(t, tenant.status)}
            </span>
          </p>
        </div>
        {writes.manageAdmin && consoleHref && (
          <a
            href={consoleHref}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-100 border border-amber-200 text-amber-600 hover:bg-amber-100 text-sm"
            data-testid="t360-open-dashboard"
          >
            <LayoutDashboard className="w-4 h-4" /> {t("t360.openDashboard")} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Financial actions toolbar: only the actions the operator may take. The custom price
          is a fact about the practice and stays visible to every reader. */}
      {(hasFinancialAction || custom_price || custom_price_pending || custom_price_ended) && (
        <div className="rounded-2xl bg-gradient-to-r from-amber-300/5 to-rose-500/5 border border-slate-200 p-4 mb-6 flex flex-wrap items-center gap-3">
          {hasFinancialAction && (
            <div className="flex flex-wrap items-center gap-3" data-testid="t360-financial-actions">
              <span className="text-xs text-slate-500 mr-2">{t("t360.financialActions")}</span>
              {canRebate && (
                <Button size="sm" variant="outline" onClick={() => setActionModal({ type: "rebate" })}
                  className="border-amber-200 text-amber-600 hover:bg-amber-100" data-testid="action-rebate">
                  <Percent className="w-3.5 h-3.5 mr-1.5" /> {t("t360.rebate")}
                </Button>
              )}
              {canAdjust && (
                <Button size="sm" variant="outline" onClick={() => setActionModal({ type: "adjust" })}
                  className="border-emerald-200 text-emerald-600 hover:bg-emerald-400/10" data-testid="action-adjust">
                  <Wallet className="w-3.5 h-3.5 mr-1.5" /> {t("t360.adjustPrice")}
                </Button>
              )}
              {canRefund && (
                <Button size="sm" variant="outline" onClick={() => setActionModal({ type: "refund" })}
                  className="border-rose-400/30 text-rose-600 hover:bg-rose-50" data-testid="action-refund">
                  <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> {t("t360.refund")}
                </Button>
              )}
            </div>
          )}
          <CustomPriceBadge
            customPrice={custom_price} canRemove={canAdjust} tenantId={tenant_id}
            onChanged={() => { refetchTenant360(); bumpHistory(); }}
          />
          <CustomPriceNotices pending={custom_price_pending} ended={custom_price_ended} />
        </div>
      )}

      {/* Subscription summary */}
      <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("t360.plan")}</div>
          <div className="font-sol font-semibold text-2xl">{plan?.name || tenant.plan_id}</div>
          {billing && (
            <>
              <div className="text-xs text-slate-500 mt-1" data-testid="t360-plan-price">
                {/* Written like every other amount on this page (tables, refund picker,
                    history, custom price): the shared Money cell's locale-invariant style. */}
                {t(`t360f.planPrice.${billing.cycle}`, {
                  price: formatMoneyCode(billing.price.cents, billing.price.currency),
                })}
              </div>
              <div className="text-xs text-slate-400" data-testid="t360-plan-cycle">
                {t(`tl.cycle.${billing.cycle}`)}
              </div>
            </>
          )}
          {notBilled && (
            <div className="text-xs text-slate-500 mt-1" data-testid="t360-plan-price">
              {t("t360f.notBilled")}
            </div>
          )}
        </div>
        {/* The two monies, never one. `platform_subscription` is what this practice PAYS
            us; `client_sales` is what it earns from its own clients — the number an
            account-health read actually wants. One figure labelled "Revenue" used to be
            the first while reading as the second. */}
        <MoneyTile
          testId="t360-paid-to-platform"
          label={t("t360.rev.toPlatform")} hint={t("t360.rev.toPlatformHint")}
          lines={revenue_30d?.platform_subscription} none={t("t360.rev.none")}
          tone="text-slate-800"
        />
        <MoneyTile
          testId="t360-client-sales"
          label={t("t360.rev.clientSales")} hint={t("t360.rev.clientSalesHint")}
          lines={revenue_30d?.client_sales} none={t("t360.rev.none")}
          tone="text-amber-600"
        />
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("t360.subscription")}</div>
          <div className="font-sol font-semibold text-xl">{subscription?.status || "—"}</div>
        </div>
        <TrialTile
          trial={data.trial}
          tenantId={tenant_id}
          tenantName={tenantDisplayName(tenant)}
          t={t}
          canAct={writes.trial}
          onChanged={refetchTenant360}
        />
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          {/* The practice's entitlement snapshot (`tenants.entitlements`): what it bought,
              which is not necessarily its plan's current matrix. The retired
              `tenants.modules` map is no longer a grant and is not counted. */}
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("t360.entitlements")}</div>
          <div className="font-sol font-semibold text-2xl" data-testid="t360-entitlements-count">
            {Array.isArray(tenant.entitlements?.capabilities) ? tenant.entitlements.capabilities.length : "—"}
          </div>
        </div>
      </div>

      <SectionsPanel tenantId={tenant_id} sections={data.sections} />

      <FinancialHistory tenantId={tenant_id} refreshToken={historyToken} />

      {actionModal && (
        <FinanceActionModal
          type={actionModal.type}
          tenantId={tenant_id}
          billingCurrency={billing_currency}
          billing={billing}
          nextInvoiceAt={data.next_invoice_at ?? null}
          onClose={() => setActionModal(null)}
          onSettled={bumpHistory}
          onDone={() => { setActionModal(null); refetchTenant360(); }}
          onStale={refetchTenant360}
        />
      )}
    </div>
  );
}
